/**
 * Returns a clean, safe copy of backsearch form fields from any source object.
 * Used both for editing existing requests and as the target format when copying.
 *
 * @param {Object|null} source - API row or null for a blank form
 * @param {boolean} clearDates - strip start_date / end_date (always true when copying)
 * @param {number} defaultMaxPostCount - fallback for max_post_count when source has none
 */
export function normalizeFormFields(source, { clearDates = false, defaultMaxPostCount = 5000 } = {}) {
  if (!source || typeof source !== 'object') {
    return {
      name: '',
      description: '',
      query: '',
      max_post_count: defaultMaxPostCount,
      providers: null,
      excluded_users: [],
      included_users: [],
      follower_count_min: null,
      follower_count_max: null,
      follower_count_operator: '',
      start_date: '',
      end_date: ''
    };
  }

  return {
    name: source.name !== null && source.name !== undefined ? String(source.name) : '',
    description: source.description !== null && source.description !== undefined ? String(source.description) : '',
    query: source.query || '',
    max_post_count: source.max_post_count || defaultMaxPostCount,
    providers: source.providers ? [...source.providers] : null,
    excluded_users: Array.isArray(source.excluded_users) ? [...source.excluded_users] : [],
    included_users: Array.isArray(source.included_users) ? [...source.included_users] : [],
    follower_count_min: source.follower_count_min !== undefined && source.follower_count_min !== null
      ? source.follower_count_min
      : null,
    follower_count_max: source.follower_count_max !== undefined && source.follower_count_max !== null
      ? source.follower_count_max
      : null,
    follower_count_operator: source.follower_count_operator || '',
    start_date: clearDates || !source.start_date ? '' : String(source.start_date).substring(0, 10),
    end_date: clearDates || !source.end_date ? '' : String(source.end_date).substring(0, 10)
  };
}

/**
 * Maps an Istarshine search config row (asian_socials_config) to backsearch form fields.
 * The config row has a single provider string, not an array, so we wrap it.
 * Name, description, and dates are intentionally cleared — the user starts fresh.
 */
export function mapSearchConfigToFormFields(row, defaultMaxPostCount = 5000) {
  if (!row || typeof row !== 'object') {
    return normalizeFormFields(null, { defaultMaxPostCount });
  }
  const provider = row.provider !== null && row.provider !== undefined ? row.provider : null;
  return normalizeFormFields({
    name: '',
    description: '',
    query: row.query || '',
    providers: provider ? [provider] : null,
    excluded_users: row.excluded_users,
    included_users: row.included_users,
    follower_count_min: row.follower_count_min,
    follower_count_max: row.follower_count_max,
    follower_count_operator: row.follower_count_operator
  }, { clearDates: true, defaultMaxPostCount });
}

/**
 * Maps an existing backsearch request to form fields for duplication.
 * Dates are cleared so the user sets a new range; name gets "-copy" to distinguish.
 */
export function mapBacksearchToFormFields(request, defaultMaxPostCount = 5000) {
  if (!request || typeof request !== 'object') {
    return normalizeFormFields(null, { defaultMaxPostCount });
  }
  const form = normalizeFormFields(request, { clearDates: true, defaultMaxPostCount });
  if (form.name) form.name = form.name + '-copy';
  return form;
}
